import React, { useState, useEffect } from 'react';
import { CourseData, CourseEntity } from 'course-types';
import { EffectiveRole, role } from 'auth-types';
import { InlineEditableText } from '@/common/components/course';
import CourseCodeDisplay from '@/common/components/course/management/CourseCodeDisplay';
import CourseUpdateService from '@/services/course/course-update-service';
import AdminCourseAccessService from '@/services/course/admin/admin-course-access-service';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';
import { useAppDispatch } from '@/lib/hooks';
import { setActiveCourseData, setCourseList } from '@/lib/slices/course-slice';
import { usePathname } from 'next/navigation';

interface AdminCoursePageInfoProps {
  course: CourseData;
  courseList: Array<CourseEntity>;
  userRole: role;
  effectiveRole: EffectiveRole | null;
  adminStudentView: boolean;
}

export function AdminCoursePageInfo({
  course,
  courseList,
  userRole,
  effectiveRole,
  adminStudentView,
}: AdminCoursePageInfoProps): React.JSX.Element {
  const dispatch = useAppDispatch();
  const pathName = usePathname();

  const [isCourseNameEditable, setIsCourseNameEditable] = useState(false);
  const [isEditingCourseName, setIsEditingCourseName] = useState(false);
  const [courseNameEditValue, setCourseNameEditValue] = useState('');

  const isAdminCoursesRoot = pathName === '/admin/courses' || pathName === '/admin/courses/';

  useEffect(() => {
    if (!course.id || !userRole) {
      setIsCourseNameEditable(false);
      return;
    }

    const editable = AdminCourseAccessService.getEditableAccess(userRole, course, effectiveRole);
    setIsCourseNameEditable(editable);
  }, [course, userRole, effectiveRole]);

  const handleCourseNameEditClick = () => {
    setIsEditingCourseName(true);
    setCourseNameEditValue(course.name ?? '');
  };

  const handleCourseNameChange = (value: string) => {
    setCourseNameEditValue(value);
  };

  const saveCourseName = async () => {
    const trimmed = courseNameEditValue.trim();

    if (!trimmed || trimmed === course.name) {
      setIsEditingCourseName(false);
      setCourseNameEditValue(course.name ?? '');
      return;
    }

    try {
      const requestResponse = await CourseUpdateService.updateCourseName(
        course.id as string,
        trimmed,
      );
      if (requestResponse.success) {
        const updatedCourse: CourseData = {
          ...course,
          name: trimmed,
        };

        dispatch(setActiveCourseData(updatedCourse));

        const updatedCourseList: Array<CourseEntity> = courseList.map((c: CourseEntity) =>
          c.id === course.id ? { ...c, name: trimmed } : c,
        );
        dispatch(setCourseList(updatedCourseList));

        await UserCourseStorageService.retrieveCourseCookie();
        const updatedCookie = UserCourseStorageService.getCourseCookie();
        if (updatedCookie) {
          dispatch(setCourseList(updatedCookie.all?.enterprise ?? []));
        }
      } else {
        setCourseNameEditValue(course.name ?? '');
      }
    } finally {
      setIsEditingCourseName(false);
    }
  };

  const handleCourseNameBlur = () => {
    void saveCourseName();
  };

  const handleCourseNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void saveCourseName();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditingCourseName(false);
      setCourseNameEditValue(course.name ?? '');
    }
  };

  return (
    <div className='flex flex-row mb-[-16px] md:px-8 lg:px-12 px-4 items-start justify-between'>
      <div className='mb-4 flex flex-col gap-1'>
        {isAdminCoursesRoot && isCourseNameEditable ? (
          <InlineEditableText
            text={course.name ?? ''}
            isEditing={isEditingCourseName}
            editValue={courseNameEditValue}
            onEditClick={handleCourseNameEditClick}
            onChange={handleCourseNameChange}
            onBlur={handleCourseNameBlur}
            onKeyDown={handleCourseNameKeyDown}
            id={course.id ?? 'courseName'}
            textElement='h1'
            textClassName='font-semibold text-2xl color-text_gray_900'
          />
        ) : (
          <h1 className='font-semibold text-2xl color-text_gray_900'>{course.name}</h1>
        )}
        {isAdminCoursesRoot && course.id && !adminStudentView && <CourseCodeDisplay />}
      </div>
    </div>
  );
}
